import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormModal } from "@packsize/components";
import { type Role } from "@packsize/identity-api-sdk";
import {
	Alert,
	Checkbox,
	Divider,
	FormControlLabel,
	FormGroup,
	Grid,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { z } from "zod";

import type { Permission } from "@/views/configuration/roles/role-utils";

export type PermissionCategory = keyof Role["permissions"];

export interface RoleConfigurationFormValues {
	name: string;
	permissions: Role["permissions"];
}

export interface RolePermissionOptions {
	Administrator: Permission[];
	Operator: Permission[];
	Reporting: Permission[];
	DIM: Permission[];
}

export interface RoleConfigurationModalTemplateProps {
	readonly open: boolean;
	readonly role?: Role;
	readonly readOnly?: boolean;
	readonly availablePermissions: RolePermissionOptions;
	readonly errorMessage?: string;
	readonly onSubmit: (data: RoleConfigurationFormValues) => Promise<void>;
	readonly onClose: () => void;
}

interface PermissionSectionProps {
	readonly title: string;
	readonly permissions: Permission[];
	readonly selectedPermissions: string[];
	readonly disabled: boolean;
	readonly onChange: (permissionName: string, checked: boolean) => void;
	readonly onToggleAll: (checked: boolean) => void;
}

const getRoleConfigurationSchema = (intl: IntlShape) =>
	z.object({
		name: z
			.string()
			.refine((name) => name.trim().length > 0, {
				message: intl.formatMessage({ id: "Name is required", defaultMessage: "Name is required" }),
			})
			.refine((name) => name.includes(",") === false, {
				message: intl.formatMessage({
					id: "Role name cannot contain commas",
					defaultMessage: "Role name cannot contain commas",
				}),
			}),
		permissions: z.object({
			Administrator: z.array(z.string()),
			Operator: z.array(z.string()),
			Reporting: z.array(z.string()),
			DIM: z.array(z.string()),
		}),
	});

const getDefaultValues = (role?: Role): RoleConfigurationFormValues => ({
	name: role?.name ?? "",
	permissions: {
		Administrator: role?.permissions.Administrator ?? [],
		Operator: role?.permissions.Operator ?? [],
		Reporting: role?.permissions.Reporting ?? [],
		DIM: role?.permissions.DIM ?? [],
	},
});

const PermissionSection = ({
	title,
	permissions,
	selectedPermissions,
	disabled,
	onChange,
	onToggleAll,
}: PermissionSectionProps) => {
	const allChecked =
		permissions.length > 0 &&
		permissions.every((permission) =>
			selectedPermissions.some(
				(selectedPermission) => selectedPermission.toLowerCase() === permission.name.toLowerCase(),
			),
		);
	const someChecked =
		allChecked === false &&
		permissions.some((permission) =>
			selectedPermissions.some(
				(selectedPermission) => selectedPermission.toLowerCase() === permission.name.toLowerCase(),
			),
		);

	return (
		<Paper variant="outlined" sx={{ height: "100%", p: 2 }}>
			<FormControlLabel
				control={
					<Checkbox
						checked={allChecked}
						indeterminate={someChecked}
						disabled={disabled}
						onChange={(event) => onToggleAll(event.target.checked)}
					/>
				}
				label={<Typography variant="subtitle1">{title}</Typography>}
			/>
			<Divider sx={{ my: 1 }} />
			<FormGroup>
				{permissions.map((permission) => (
					<FormControlLabel
						key={permission.name}
						control={
							<Checkbox
								checked={selectedPermissions.some(
									(selectedPermission) => selectedPermission.toLowerCase() === permission.name.toLowerCase(),
								)}
								disabled={disabled}
								onChange={(event) => onChange(permission.name, event.target.checked)}
								sx={{ py: 0.5 }}
							/>
						}
						label={
							<Typography variant="body2">
								<FormattedMessage
									id={permission.text}
									defaultMessage="{permission}"
									values={{ permission: permission.text }}
								/>
							</Typography>
						}
					/>
				))}
			</FormGroup>
		</Paper>
	);
};

export const RoleConfigurationModalTemplate = ({
	open,
	role,
	readOnly = false,
	availablePermissions,
	errorMessage,
	onSubmit,
	onClose,
}: RoleConfigurationModalTemplateProps) => {
	const intl = useIntl();
	const {
		control,
		handleSubmit,
		register,
		reset,
		setValue,
		formState: { errors, isSubmitting },
	} = useForm<RoleConfigurationFormValues>({
		resolver: zodResolver(getRoleConfigurationSchema(intl)),
		defaultValues: getDefaultValues(role),
	});
	const permissions = useWatch({ control, name: "permissions" });

	useEffect(() => {
		if (open === true) {
			reset(getDefaultValues(role));
		}
	}, [open, reset, role]);

	const handleClose = () => {
		reset(getDefaultValues(role));
		onClose();
	};

	const setPermissions = (category: PermissionCategory, values: string[]) => {
		switch (category) {
			case "Administrator":
				setValue("permissions.Administrator", values, { shouldDirty: true, shouldValidate: true });
				break;
			case "Operator":
				setValue("permissions.Operator", values, { shouldDirty: true, shouldValidate: true });
				break;
			case "Reporting":
				setValue("permissions.Reporting", values, { shouldDirty: true, shouldValidate: true });
				break;
			case "DIM":
				setValue("permissions.DIM", values, { shouldDirty: true, shouldValidate: true });
				break;
		}
	};

	const handlePermissionChange = (category: PermissionCategory) => (permissionName: string, checked: boolean) => {
		const currentPermissions = permissions[category];
		const updatedPermissions = checked
			? currentPermissions.some((permission) => permission.toLowerCase() === permissionName.toLowerCase())
				? currentPermissions
				: [...currentPermissions, permissionName]
			: currentPermissions.filter((permission) => permission.toLowerCase() !== permissionName.toLowerCase());

		setPermissions(category, updatedPermissions);

		if (category === "DIM" && permissionName === "ManageProducts") {
			const viewProductsSelected = updatedPermissions.some((permission) => permission.toLowerCase() === "viewproducts");
			if (checked === true && viewProductsSelected === false) {
				setPermissions(category, [...updatedPermissions, "ViewProducts"]);
			}
			if (checked === false && viewProductsSelected === true) {
				setPermissions(
					category,
					updatedPermissions.filter((permission) => permission.toLowerCase() !== "viewproducts"),
				);
			}
		}
	};

	const handleToggleAll = (category: PermissionCategory, available: Permission[]) => (checked: boolean) => {
		setPermissions(category, checked === true ? available.map((permission) => permission.name) : []);
	};

	const visibleDIMPermissions = availablePermissions.DIM.filter(
		(permission) =>
			permission.name !== "ViewProducts" ||
			permissions.DIM.some((selectedPermission) => selectedPermission.toLowerCase() === "manageproducts"),
	);

	return (
		<FormModal
			open={open}
			title={
				role?.id
					? intl.formatMessage({ id: "Edit Role", defaultMessage: "Edit Role" })
					: intl.formatMessage({ id: "New Role", defaultMessage: "New Role" })
			}
			onSubmit={handleSubmit(onSubmit)}
			size="xl"
			submitText={intl.formatMessage({ id: "Save", defaultMessage: "Save" })}
			cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
			onClose={handleClose}
			onCancel={handleClose}
			slotProps={{
				confirmButton: {
					disabled: readOnly || isSubmitting,
					loading: isSubmitting,
					"data-testid": "role-configuration-modal__button__confirm",
					"data-pendoid": "role-configuration-modal__button__confirm",
					sx: readOnly === true ? { display: "none" } : undefined,
				},
				cancelButton: {
					"data-testid": "role-configuration-modal__button__cancel",
					"data-pendoid": "role-configuration-modal__button__cancel",
				},
			}}
		>
			<Grid container spacing={2}>
				{readOnly === true && (
					<Grid size={12}>
						<Alert severity="warning">
							<FormattedMessage
								id="This role belongs to another site in this company. You can assign it to users, but will not be able to modify the role."
								defaultMessage="This role belongs to another site in this company. You can assign it to users, but will not be able to modify the role."
							/>
						</Alert>
					</Grid>
				)}
				<Grid size={12}>
					<TextField
						{...register("name")}
						disabled={readOnly}
						fullWidth
						label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
						placeholder={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
						error={errors.name !== undefined}
						helperText={errors.name?.message}
						slotProps={{
							htmlInput: {
								"data-testid": "role-configuration-modal__input__name",
							},
							formHelperText: {
								"data-testid": "role-configuration-modal__input__name-error",
							},
						}}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<PermissionSection
						title={intl.formatMessage({ id: "Operator Permissions", defaultMessage: "Operator Permissions" })}
						permissions={availablePermissions.Operator}
						selectedPermissions={permissions.Operator}
						disabled={readOnly}
						onChange={handlePermissionChange("Operator")}
						onToggleAll={handleToggleAll("Operator", availablePermissions.Operator)}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<PermissionSection
						title={intl.formatMessage({ id: "DIM Permissions", defaultMessage: "DIM Permissions" })}
						permissions={visibleDIMPermissions}
						selectedPermissions={permissions.DIM}
						disabled={readOnly}
						onChange={handlePermissionChange("DIM")}
						onToggleAll={handleToggleAll("DIM", visibleDIMPermissions)}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<PermissionSection
						title={intl.formatMessage({ id: "Reporting Permissions", defaultMessage: "Reporting Permissions" })}
						permissions={availablePermissions.Reporting}
						selectedPermissions={permissions.Reporting}
						disabled={readOnly}
						onChange={handlePermissionChange("Reporting")}
						onToggleAll={handleToggleAll("Reporting", availablePermissions.Reporting)}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<PermissionSection
						title={intl.formatMessage({
							id: "Administrator Permissions",
							defaultMessage: "Administrator Permissions",
						})}
						permissions={availablePermissions.Administrator}
						selectedPermissions={permissions.Administrator}
						disabled={readOnly}
						onChange={handlePermissionChange("Administrator")}
						onToggleAll={handleToggleAll("Administrator", availablePermissions.Administrator)}
					/>
				</Grid>
				{Boolean(errorMessage) === true && (
					<Grid size={12}>
						<Typography color="error" data-testid="role-configuration-modal__save__error">
							{errorMessage}
						</Typography>
					</Grid>
				)}
			</Grid>
		</FormModal>
	);
};
